import { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Check, ChevronDown, Calendar, Clock, MapPin, Users,
  Award, Utensils, Shield, ShieldCheck, Heart, Lock, Tag as TagIcon,
  CheckCircle2, AlertTriangle, MessageCircle, Star, Package, CreditCard,
  Fingerprint, KeyRound, EyeOff, User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_db2be5c0-fcfc-48d7-8252-f22b445a2705/artifacts/uqf9sd9c_LOGO%20FOR%20MINI%20MAKERS.png";

const C = {
  purple: "#5B2A86",
  purpleDeep: "#3F1A66",
  gold: "#F5C518",
  orange: "#F2762E",
  pink: "#E91E80",
  green: "#3FA437",
  teal: "#0FA9A0",
  blue: "#1F8BD9",
  cream: "#FAF6EC",
};

const PRODUCTS = [
  { name: "Soap (Custom Moulds)", icon: "🧼", desc: "Pour, swirl, and shape unique soaps in fun designs." },
  { name: "Lip Balm", icon: "💋", desc: "Whisk, scent, and pour into your own labelled tubes." },
  { name: "Sugar Scrubs", icon: "🍯", desc: "Mix sweet-scented exfoliating scrubs in jars." },
  { name: "Bath Salts", icon: "🛁", desc: "Layer colourful bath salt blends with custom scents." },
  { name: "Herbal Soak", icon: "🌿", desc: "Combine dried botanicals into a luxurious soak." },
  { name: "Body Oil", icon: "✨", desc: "Blend nourishing oils and pour into apothecary bottles." },
  { name: "Body Butter", icon: "🧈", desc: "Whip rich, creamy body butter in your own pot." },
  { name: "Cuticle Oil", icon: "💅", desc: "Mix tiny bottles of scented cuticle oil." },
  { name: "Perfume Oil", icon: "🌸", desc: "Design your own signature perfume oil blend." },
];

// Default age group cards — bullets/colors stay constant; labels & titles overlay from live settings via buildAgeGroups()
const AGE_GROUPS_DEFAULT = [
  {
    key: "4-7",
    label: "Ages 4–7",
    title: "Little Makers",
    color: C.pink,
    desc: "More guided, playful, and simplified — gentle pours, easy mixing, big imaginations.",
    bullets: ["Extra hands-on supervision", "Simplified product steps", "Sensory-friendly stations", "Mini market showcase"],
  },
  {
    key: "8-11",
    label: "Ages 8–11",
    title: "Junior Makers",
    color: C.orange,
    desc: "Hands-on confidence builders — mixing, scenting, packaging, and branding their own products.",
    bullets: ["Full product line participation", "Branding & label design", "Creative independence", "Showcase presentation skills"],
  },
  {
    key: "12-16",
    label: "Ages 12–16",
    title: "Senior Makers",
    color: C.teal,
    desc: "Elevated products, business basics, and confident showcasing — real-world maker experience.",
    bullets: ["Advanced product variations", "Personal brand creation", "Pricing & presentation", "Mentor-supported leadership"],
  },
];

// Builds the age-group card data using live admin settings (titles + age labels)
const buildAgeGroups = (settings) => {
  if (!settings) return AGE_GROUPS_DEFAULT;
  const titleByKey = {
    "4-7": settings.group_title_4_7,
    "8-11": settings.group_title_8_11,
    "12-16": settings.group_title_12_16,
  };
  const labelByKey = {
    "4-7": settings.group_age_label_4_7,
    "8-11": settings.group_age_label_8_11,
    "12-16": settings.group_age_label_12_16,
  };
  return AGE_GROUPS_DEFAULT.map((g) => ({
    ...g,
    label: labelByKey[g.key] || g.label,
    title: titleByKey[g.key] || g.title,
  }));
};

const WHATS_INCLUDED = [
  "All premium materials & tools",
  "Guided, age-grouped activities",
  "Take-home products in branded packaging",
  "Showcase Day participation",
  "Personalised certificate of completion",
  "Food, snacks & drinks daily",
  "Supervised, safe environment",
  "Age-grouped sessions for safety",
];

const FAQS_DEFAULT = [
  { q: "What is included in the fee?", a: "Everything: all materials, age-grouped guided activities, take-home products, Showcase Day participation, a certificate, and food/snacks/drinks each day." },
  { q: "Is food provided?", a: "Yes — food, snacks, and drinks are provided. Parents must fully disclose any allergies, dietary restrictions, or food-related medical needs at registration." },
  { q: "What if my child has allergies or dietary restrictions?", a: "Please complete the allergies/dietary/medical section at registration in full. We rely on the accuracy of this information. While we minimise cross-contact, we cannot guarantee an allergen-free environment. For severe allergies/anaphylaxis, please provide clear instructions and any required medication." },
  { q: "Do parents have to attend the Showcase?", a: "Yes. Parents/guardians are expected to attend the final Showcase Day to support and cheer for their child. This is part of the camp experience and registration acknowledges this requirement." },
  { q: "Is full payment required?", a: "Yes. Full payment is required to reserve your child's spot. Spots are limited." },
  { q: "What is the refund policy?", a: "Mini Makers Market Camp registrations are non-refundable. Once you register, we immediately commit to purchasing materials, ingredients, food, and craft supplies for your child. If your plans change, we are happy to transfer your spot to another family — just contact us as early as possible." },
  { q: "What should my child bring?", a: "Just themselves and their creativity. We provide everything. We recommend comfortable clothing they don't mind getting a little messy in." },
  { q: "Can siblings attend together?", a: "Absolutely — and we offer a sibling discount. Siblings will be placed in their appropriate age group during activities but reconnect for shared moments." },
  { q: "What happens if my child needs extra support?", a: "Please let us know about any sensory, behavioural, or support needs at registration. We'll work with you to make the experience successful. Organisers may contact parents if a child needs additional support during the day." },
  { q: "Who can pick up my child?", a: "Only individuals listed on your authorised pickup list. Photo ID and a matching selfie are required (uploaded at registration). Late or unauthorised pickups will trigger a call to the parents/emergency contacts." },
];

// Builds the FAQ list dynamically using live admin settings (age range, group labels, etc.)
const buildFaqs = (settings) => {
  if (!settings) return FAQS_DEFAULT;
  const ageRange = (settings.age_range_label || "Ages 4–16").replace(/^ages\s*/i, "");
  const g1 = settings.group_age_label_4_7 || "Ages 4–7";
  const g2 = settings.group_age_label_8_11 || "Ages 8–11";
  const g3 = settings.group_age_label_12_16 || "Ages 12–16";
  const stripAges = (s) => s.replace(/^ages\s*/i, "");
  const bands = `${stripAges(g1)}, ${stripAges(g2)}, ${stripAges(g3)}`;
  return [
    { q: "What ages can attend?", a: `Mini Makers Market Camp is open to children ages ${ageRange}. We carefully group children by age (${bands}) to make sure every camper has age-appropriate guidance and activities.` },
    { q: "Are children grouped by age?", a: `Yes. Children are always grouped into one of three age bands (${bands}). This keeps activities safe, age-appropriate, and fun.` },
    ...FAQS_DEFAULT,
  ];
};

const EMPTY_CHILD = {
  full_name: "", age: "", date_of_birth: "", age_group: "",
  medical_conditions: "", allergies: "", dietary_restrictions: "",
  medications: "", support_needs: "", photo_consent: false,
};

const EMPTY_CONSENTS = {
  consent_legal_guardian: false,
  consent_info_accurate: false,
  consent_food_allergy_disclosed: false,
  consent_emergency_medical: false,
  consent_authorized_pickup: false,
  consent_showcase_attendance: false,
  consent_full_payment: false,
  consent_policies_waiver: false,
  consent_hands_on_activities: false,
  consent_organizers_authority: false,
};

const ageGroupForAge = (age) => {
  const a = parseInt(age, 10);
  if (!a) return "";
  if (a >= 4 && a <= 7) return "4-7";
  if (a >= 8 && a <= 11) return "8-11";
  if (a >= 12 && a <= 16) return "12-16";
  return "";
};

const MarketCampPage = () => {
  const [settings, setSettings] = useState(null);
  const [spots, setSpots] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [openPolicy, setOpenPolicy] = useState(null);
  const [numChildren, setNumChildren] = useState(1);
  const [children, setChildren] = useState([{ ...EMPTY_CHILD }]);
  const [parent, setParent] = useState({
    parent_full_name: "", parent_email: "", parent_phone: "",
    emergency_contact_name: "", emergency_contact_phone: "",
    secondary_emergency_name: "", secondary_emergency_phone: "",
    authorized_pickup: "",
  });
  const [consents, setConsents] = useState({ ...EMPTY_CONSENTS });
  const [whatsappOptIn, setWhatsappOptIn] = useState(true);
  const [referralCode, setReferralCode] = useState("");
  const [referralStatus, setReferralStatus] = useState({ state: "idle", message: "" }); // idle | checking | valid | invalid
  const [couponCode, setCouponCode] = useState("");
  const [couponStatus, setCouponStatus] = useState({ state: "idle", message: "", discount_amount: 0, discount_label: "", description: "" });
  // Pickup verification photos (stored as object-storage paths after upload)
  const [pickupPhotos, setPickupPhotos] = useState({
    id: { path: "", content_type: "", preview: "", uploading: false },
    selfie: { path: "", content_type: "", preview: "", uploading: false },
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    document.title = "Mini Makers Market Camp · Register Now | Mix & Mingle Events";
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute(
      "content",
      "Mini Makers Market Camp — a premium summer experience for ages 4–16. Kids create real spa products (soap, lip balm, body butter and more), build their brand, and showcase their work. Includes food, materials, certificate. Sibling discounts available."
    );
    axios.get(`${API}/public/market-camp-settings`).then(r => setSettings(r.data)).catch(() => {});
    axios.get(`${API}/public/market-camp/spots`).then(r => setSpots(r.data)).catch(() => {});
    // Pre-fill referral code from URL ?ref=CODE
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim().toUpperCase();
      if (ref) setReferralCode(ref);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    setChildren(prev => {
      const next = [...prev];
      if (numChildren > next.length) {
        while (next.length < numChildren) next.push({ ...EMPTY_CHILD });
      } else if (numChildren < next.length) {
        next.length = numChildren;
      }
      return next;
    });
  }, [numChildren]);

  const pricing = useMemo(() => {
    if (!settings) return null;
    const per = settings.early_bird_active ? settings.price_early_bird : settings.price_regular;
    const subtotal = per * numChildren;
    let pct = 0;
    if (numChildren === 2) pct = settings.sibling_discount_2 || 0;
    else if (numChildren === 3) pct = settings.sibling_discount_3 || 0;
    else if (numChildren === 4) pct = settings.sibling_discount_4 || 0;
    const discountAmount = +(subtotal * pct / 100).toFixed(2);
    const total = +(subtotal - discountAmount).toFixed(2);
    return { per, subtotal, pct, discountAmount, total };
  }, [settings, numChildren]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const updateChild = (i, field, value) => {
    setChildren(prev => {
      const next = [...prev];
      const updated = { ...next[i], [field]: value };
      if (field === "age") updated.age_group = ageGroupForAge(value);
      next[i] = updated;
      return next;
    });
  };

  const allConsentsChecked = Object.values(consents).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Parent validation
    if (!parent.parent_full_name || !parent.parent_email || !parent.parent_phone ||
        !parent.emergency_contact_name || !parent.emergency_contact_phone ||
        !parent.authorized_pickup) {
      toast.error("Please complete all parent & emergency contact fields.");
      return;
    }
    // Children validation
    for (let i = 0; i < children.length; i += 1) {
      const c = children[i];
      if (!c.full_name?.trim() || !c.age || !c.age_group) {
        toast.error(`Please complete child ${i + 1}'s name, age, and age-group.`);
        return;
      }
      const a = parseInt(c.age, 10);
      if (a < 4 || a > 16) {
        toast.error(`Child ${i + 1}'s age must be between 4 and 16.`);
        return;
      }
    }
    if (!allConsentsChecked) {
      toast.error("All 10 required consents must be acknowledged before checkout.");
      return;
    }
    // Pickup verification photos required (safety)
    if (!pickupPhotos.id.path) {
      toast.error("Please upload a photo of the pickup person's government-issued ID.");
      document.getElementById("pickup-verification-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (!pickupPhotos.selfie.path) {
      toast.error("Please upload a selfie of the pickup person (face clearly visible).");
      document.getElementById("pickup-verification-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...parent,
        children,
        ...consents,
        whatsapp_confirmation_opt_in: whatsappOptIn,
        referral_code: (referralCode || "").trim().toUpperCase(),
        coupon_code: (couponStatus.state === "valid" ? (couponCode || "").trim().toUpperCase() : ""),
        pickup_id_storage_path: pickupPhotos.id.path || "",
        pickup_selfie_storage_path: pickupPhotos.selfie.path || "",
        pickup_id_content_type: pickupPhotos.id.content_type || "",
        pickup_selfie_content_type: pickupPhotos.selfie.content_type || "",
        origin_url: window.location.origin,
      };
      const res = await axios.post(`${API}/public/market-camp/checkout`, payload);
      // Redirect to Stripe
      window.location.href = res.data.checkout_url;
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout could not be created. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout could not be created.");
      setSubmitting(false);
    }
  };

  if (!settings) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: C.cream }}>
        <div className="w-10 h-10 border-4 border-[#5B2A86] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const earlyBird = settings.early_bird_active;

  return (
    <div className="min-h-screen" style={{ backgroundColor: C.cream, fontFamily: "'Nunito', 'Quicksand', system-ui, sans-serif" }} data-testid="market-camp-page">
      {/* ===== HERO ===== */}
      <header
        className="relative overflow-hidden"
        style={{ background: `linear-gradient(180deg, ${C.purple} 0%, ${C.purpleDeep} 100%)` }}
        data-testid="market-camp-hero"
      >
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          {[...Array(18)].map((_, i) => (
            <div key={i} className="absolute rounded-full" style={{
              width: `${6 + (i % 4) * 4}px`, height: `${6 + (i % 4) * 4}px`,
              background: [C.gold, C.orange, C.pink, C.green, C.teal][i % 5],
              top: `${(i * 53) % 100}%`, left: `${(i * 37) % 100}%`,
            }} />
          ))}
        </div>
        <div className="relative max-w-6xl mx-auto px-6 py-10 sm:py-14">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6 }} className="flex justify-center lg:justify-start">
              <img src={LOGO_URL} alt="Mini Makers Market Camp" className="w-full max-w-md drop-shadow-2xl" data-testid="market-camp-logo" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.1 }} className="text-center lg:text-left text-white">
              <span className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4" style={{ backgroundColor: C.gold, color: C.purpleDeep }}>
                Summer 2026 · Registration Open
              </span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black leading-tight mb-4">
                {settings.hero_headline ? (
                  settings.hero_headline
                ) : (
                  <>Where kids ages <span style={{ color: C.gold }}>{(settings.age_range_label || "Ages 4–16").replace(/^ages\s*/i, "")}</span> create real spa products, build their brand, and showcase like <span style={{ color: C.pink }}>pros</span>.</>
                )}
              </h1>
              <p className="text-base sm:text-lg text-white/80 mb-5">
                {settings.hero_subheadline || "3 days of hands-on making, creativity, and confidence — ending with a real Market Showcase. Food, materials, and certificate included."}
              </p>
              <div className="space-y-2 mb-6">
                <div className="grid sm:grid-cols-2 gap-2.5">
                  <div className="rounded-xl px-3 py-2.5" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)" }}>
                    <p className="text-[10px] uppercase tracking-wider text-white/70">Workshop Days</p>
                    <p className="text-sm font-bold leading-tight">Aug 13–14 · 10AM–4PM</p>
                  </div>
                  <div className="rounded-xl px-3 py-2.5" style={{ background: `${C.gold}20`, border: `1.5px solid ${C.gold}` }}>
                    <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: C.gold }}>⭐ Showcase Day</p>
                    <p className="text-sm font-bold leading-tight">Aug 15 · 1PM–Finish</p>
                    <p className="text-[10px] text-white/70 mt-0.5">Parents must attend!</p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  <div className="rounded-xl px-3 py-2.5 text-center" style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)" }}>
                    <p className="text-[10px] uppercase tracking-wider text-white/70">Ages</p>
                    <p className="text-sm font-bold">{(settings.age_range_label || "Ages 4–16").replace(/^ages\s*/i, "")} · 3 Groups</p>
                  </div>
                  <div className="rounded-xl px-3 py-2.5 text-center" style={{ background: "rgba(245,197,24,0.18)", border: "1px solid rgba(245,197,24,0.45)" }}>
                    <p className="text-[10px] uppercase tracking-wider" style={{ color: C.gold }}>From</p>
                    <p className="text-sm font-bold">${earlyBird ? settings.price_early_bird : settings.price_regular}/child · 3 days</p>
                    <p className="text-[10px] text-white/70 mt-0.5">≈ ${(((earlyBird ? settings.price_early_bird : settings.price_regular) / 3)).toFixed(2)}/day · food + materials included</p>
                  </div>
                </div>
                {/* Location card */}
                <a
                  href="https://www.google.com/maps/search/?api=1&query=11560+149+St+NW+Edmonton"
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-xl px-3 py-2.5 flex items-center gap-2.5 hover:bg-white/10 transition-colors"
                  style={{ background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)" }}
                  data-testid="hero-location-card"
                >
                  <MapPin className="w-5 h-5 flex-shrink-0" style={{ color: C.gold }} />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] uppercase tracking-wider text-white/70">Location</p>
                    <p className="text-sm font-bold leading-tight">{settings.address_line || settings.location_label || "11560 149 St NW, Edmonton, AB"}</p>
                  </div>
                  <span className="text-[10px] text-white/60 font-bold uppercase tracking-wider hidden sm:inline">Map →</span>
                </a>
              </div>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => scrollTo("register")} className="px-7 py-3.5 rounded-full font-extrabold text-base shadow-xl hover:-translate-y-0.5 transition-all" style={{ backgroundColor: C.gold, color: C.purpleDeep }} data-testid="hero-register-btn">
                  Register Now →
                </button>
                <button onClick={() => scrollTo("details")} className="px-7 py-3.5 rounded-full font-extrabold text-base border-2 border-white/40 text-white hover:bg-white/10 transition-all" data-testid="hero-details-btn">
                  View Program Details
                </button>
              </div>
              {/* Spots-Remaining Live Counter */}
              <SpotsRemainingHero spots={spots} />
              <p className="mt-3 text-xs text-white/60 flex items-center justify-center lg:justify-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> {settings.spots_left_label}
              </p>
            </motion.div>
          </div>
        </div>
      </header>

      {/* ===== WHAT CHILDREN WILL MAKE ===== */}
      <section className="py-16 px-6" id="details" data-testid="products-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.purple }}>
              What Children Will Make
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>
              Real products. Real brands. Real pride.
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              Each child crafts a beautiful range of cosmetic spa products to take home — and brings the experience to life on Showcase Day.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-4">
            {PRODUCTS.map((p, i) => (
              <motion.div
                key={p.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04 }}
                className="bg-white rounded-2xl p-5 border-2 shadow-sm hover:shadow-md transition-all"
                style={{ borderColor: `${C.purple}15` }}
                data-testid={`product-card-${i}`}
              >
                <div className="text-3xl mb-2">{p.icon}</div>
                <h3 className="font-extrabold text-gray-900 text-sm sm:text-base mb-1">{p.name}</h3>
                <p className="text-xs sm:text-sm text-gray-600 leading-snug">{p.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 mt-6 italic">
            All products are cosmetic and creative — no medical or therapeutic claims are made.
          </p>
        </div>
      </section>

      {/* ===== AGE GROUPS ===== */}
      <section className="py-16 px-6 bg-white" data-testid="age-groups-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.teal }}>
              Three Age Groups
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>
              Grouped by age. Designed for every stage.
            </h2>
            <p className="text-gray-600 mt-3 max-w-2xl mx-auto">
              Every camper is placed in a small, age-matched group so activities are safe, supportive, and age-appropriate.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {buildAgeGroups(settings).map((g, i) => (
              <motion.div
                key={g.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="rounded-3xl p-6 border-2 shadow-md hover:-translate-y-1 transition-all"
                style={{ borderColor: `${g.color}40`, backgroundColor: C.cream }}
                data-testid={`age-group-${i}`}
              >
                <span className="inline-block px-3 py-1 rounded-full text-xs font-extrabold text-white mb-3" style={{ backgroundColor: g.color }}>
                  {g.label}
                </span>
                <h3 className="font-black text-xl mb-2" style={{ color: g.color }}>{g.title}</h3>
                <p className="text-sm text-gray-700 leading-relaxed mb-4">{g.desc}</p>
                <ul className="space-y-2">
                  {g.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: g.color }} strokeWidth={3} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHAT'S INCLUDED ===== */}
      <section className="py-16 px-6" data-testid="included-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.green }}>
              What's Included
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>
              One price. Everything covered.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {WHATS_INCLUDED.map((item, i) => (
              <div key={i} className="flex items-start gap-3 bg-white rounded-xl px-4 py-3 border-2" style={{ borderColor: `${C.green}25` }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: C.green }}>
                  <Check className="w-4 h-4 text-white" strokeWidth={3} />
                </div>
                <span className="text-sm sm:text-base font-semibold text-gray-800">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING & SIBLING DEALS ===== */}
      <section className="py-16 px-6 bg-white" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.pink }}>
              Family Pricing
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>Bring siblings — save more.</h2>
            <p className="text-gray-600 mt-3">Full payment required to secure each child's spot.</p>
          </div>

          {earlyBird && (
            <div className="text-center mb-6">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-extrabold" style={{ backgroundColor: `${C.gold}30`, color: C.purpleDeep, border: `2px solid ${C.gold}` }}>
                <Star className="w-4 h-4" fill={C.gold} /> EARLY BIRD ACTIVE · {settings.early_bird_ends_label}
              </span>
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {[
              { kids: 1, pct: 0, badge: null },
              { kids: 2, pct: settings.sibling_discount_2, badge: "Popular" },
              { kids: 3, pct: settings.sibling_discount_3, badge: "Best Value" },
              { kids: 4, pct: settings.sibling_discount_4, badge: "Big Family" },
              { kids: "5+", pct: null, badge: "Custom", contact: true },
            ].map((tier, i) => {
              const isContact = tier.contact;
              const per = earlyBird ? settings.price_early_bird : settings.price_regular;
              const subtotal = isContact ? null : per * tier.kids;
              const total = isContact ? null : +(subtotal - (subtotal * tier.pct / 100)).toFixed(2);
              const highlight = tier.kids === 2;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.06 }}
                  className={`relative rounded-3xl p-5 border-2 ${highlight ? "shadow-xl scale-[1.02]" : "shadow-md"}`}
                  style={{ borderColor: highlight ? C.purple : `${C.purple}15`, backgroundColor: highlight ? C.purple : C.cream, color: highlight ? "white" : "#1A1A1A" }}
                  data-testid={`pricing-tier-${i}`}
                >
                  {tier.badge && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-extrabold tracking-wider uppercase" style={{ backgroundColor: C.gold, color: C.purpleDeep }}>
                      {tier.badge}
                    </span>
                  )}
                  <p className="text-xs uppercase font-bold tracking-wider mb-1" style={{ color: highlight ? C.gold : C.purple }}>
                    {tier.kids === 1 ? "1 Child" : tier.kids === "5+" ? "5+ Children" : `${tier.kids} Children`}
                  </p>
                  {isContact ? (
                    <>
                      <p className="text-3xl font-black mb-1">Contact us</p>
                      <p className="text-xs opacity-75 mb-4">Custom family rate</p>
                    </>
                  ) : (
                    <>
                      <p className="text-3xl font-black leading-none mb-1">${total.toFixed(2)}</p>
                      <p className="text-[10px] opacity-70 mb-2">total for 3 days · {tier.kids} {tier.kids === 1 ? "child" : "children"}</p>
                      {/* BIG, BOLD per-day price — makes it feel affordable */}
                      <div
                        className="rounded-xl px-3 py-2 mb-2 inline-block"
                        style={{
                          background: highlight ? "rgba(245,197,24,0.18)" : `${C.gold}22`,
                          border: highlight ? `1.5px solid ${C.gold}80` : `1.5px solid ${C.gold}80`,
                        }}
                      >
                        <p className="text-[9px] uppercase tracking-wider font-extrabold leading-none mb-0.5" style={{ color: highlight ? C.gold : C.purpleDeep }}>
                          Just per day, per child
                        </p>
                        <p className="text-2xl font-black leading-none" style={{ color: highlight ? C.gold : C.purpleDeep }}>
                          ${(total / (3 * tier.kids)).toFixed(2)}<span className="text-xs font-bold opacity-70">/day</span>
                        </p>
                      </div>
                      {tier.pct > 0 ? (
                        <p className="text-xs font-extrabold mb-3" style={{ color: highlight ? C.gold : C.green }}>
                          {tier.pct}% off · Save ${(subtotal * tier.pct / 100).toFixed(0)}
                        </p>
                      ) : (
                        <p className="text-xs opacity-60 mb-3">Standard price</p>
                      )}
                    </>
                  )}
                  <ul className="space-y-1.5 text-sm">
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Full 3 days</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Food, snacks & drinks</li>
                    <li className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Age-grouped + Showcase</li>
                  </ul>
                </motion.div>
              );
            })}
          </div>
          <div className="text-center mt-8">
            <button onClick={() => scrollTo("register")} className="px-8 py-4 rounded-full font-extrabold text-base text-white shadow-lg hover:-translate-y-0.5 transition-all" style={{ backgroundColor: C.purple }} data-testid="pricing-register-btn">
              Register & Pay →
            </button>
          </div>
        </div>
      </section>

      {/* ===== SHOWCASE ===== */}
      <section className="py-16 px-6" style={{ backgroundColor: `${C.purple}06` }} data-testid="showcase-section">
        <div className="max-w-4xl mx-auto rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.purple} 0%, ${C.purpleDeep} 100%)` }}>
          <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-20" style={{ backgroundColor: C.gold }} />
          <span className="inline-block px-3 py-1 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4" style={{ backgroundColor: C.gold, color: C.purpleDeep }}>
            Final Day Showcase
          </span>
          <h2 className="text-2xl sm:text-4xl font-black mb-3">Cheer them on. Celebrate their work.</h2>
          <p className="text-base sm:text-lg text-white/85 mb-4 leading-relaxed">
            On <strong>{settings.showcase_label}</strong>, each child presents the products they've made and the brand they've built. It's a real Market Showcase — confident, creative, and unforgettable.
          </p>
          <div className="inline-flex items-start gap-3 rounded-2xl px-4 py-3" style={{ backgroundColor: "rgba(245,197,24,0.18)", border: `2px solid ${C.gold}` }}>
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: C.gold }} />
            <p className="text-sm sm:text-base font-semibold">
              Parents/guardians are <span className="underline">expected to attend</span> the Showcase to support and celebrate their child. Registration acknowledges this requirement.
            </p>
          </div>
        </div>
      </section>

      {/* ===== FOOD / ALLERGY ===== */}
      <section className="py-16 px-6 bg-white" data-testid="food-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.orange }}>
              Food, Snacks & Drinks
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>Fueled, fed, and looked after.</h2>
          </div>
          <div className="rounded-3xl p-6 sm:p-8 border-2 space-y-4 text-sm sm:text-base leading-relaxed text-gray-800" style={{ borderColor: `${C.orange}30`, backgroundColor: C.cream }}>
            <p><Utensils className="inline w-5 h-5 mr-2" style={{ color: C.orange }} /> Food, snacks, and drinks are provided throughout the camp at no extra cost.</p>
            <p><strong className="text-[#5B2A86]">Allergy & dietary disclosure required.</strong> Parents/guardians must disclose all allergies, intolerances, dietary restrictions, and medical food-related needs during registration.</p>
            <p>We will not knowingly serve food contrary to your disclosed restrictions, and we may limit, substitute, or withhold items for safety reasons.</p>
            <p>For severe allergies or anaphylaxis risk, please provide clear instructions and any required medication at registration. We minimise cross-contact but cannot guarantee an allergen-free environment.</p>
            <p>If a child has an allergic reaction or medical issue, we follow our emergency procedures and may contact emergency services and your emergency contacts immediately.</p>
          </div>
        </div>
      </section>

      {/* ===== SAFETY ===== */}
      <section className="py-16 px-6" data-testid="safety-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.blue }}>
              Safety & Parent Info
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>Structure. Supervision. Respect.</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: Shield, t: "Supervised environment", d: "All sessions are supervised by trained organisers." },
              { icon: Users, t: "Age-grouped sessions", d: "Children are placed in age-appropriate groups (4–7, 8–11, 12–16)." },
              { icon: Heart, t: "Emergency contacts", d: "Primary and secondary emergency contacts are collected at registration." },
              { icon: Lock, t: "Authorised pickup only", d: "Only the people you list may pick up your child. Photo ID may be required." },
              { icon: AlertTriangle, t: "Medical & allergy disclosure", d: "Full disclosure at registration is required for safety." },
              { icon: CheckCircle2, t: "Respectful conduct", d: "We expect kind, respectful behavior from all campers, parents, and staff." },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border-2 flex items-start gap-3" style={{ borderColor: `${C.blue}20` }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${C.blue}18` }}>
                  <s.icon className="w-5 h-5" style={{ color: C.blue }} />
                </div>
                <div>
                  <p className="font-extrabold text-gray-900 mb-1">{s.t}</p>
                  <p className="text-sm text-gray-600 leading-snug">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-500 mt-6 text-center">
            Organisers reserve the right to contact a parent/guardian if a child requires support, pickup, or removal for safety, conduct, or supervision reasons.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-16 px-6 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.purple }}>
              FAQ
            </span>
            <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>Frequently asked questions</h2>
          </div>
          <div className="space-y-2">
            {buildFaqs(settings).map((f, i) => (
              <div key={i} className="rounded-2xl border-2 overflow-hidden" style={{ borderColor: `${C.purple}15` }} data-testid={`faq-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left bg-white hover:bg-gray-50 transition-colors"
                >
                  <span className="font-bold text-gray-900 text-sm sm:text-base">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} style={{ color: C.purple }} />
                </button>
                <AnimatePresence initial={false}>
                  {openFaq === i && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                      style={{ backgroundColor: C.cream }}
                    >
                      <p className="px-5 py-4 text-sm sm:text-base text-gray-700 leading-relaxed">{f.a}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== REGISTRATION FORM ===== */}
      <RegistrationForm
        id="register"
        settings={settings}
        numChildren={numChildren}
        setNumChildren={setNumChildren}
        children={children}
        updateChild={updateChild}
        parent={parent}
        setParent={setParent}
        consents={consents}
        setConsents={setConsents}
        whatsappOptIn={whatsappOptIn}
        setWhatsappOptIn={setWhatsappOptIn}
        referralCode={referralCode}
        setReferralCode={setReferralCode}
        referralStatus={referralStatus}
        setReferralStatus={setReferralStatus}
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        couponStatus={couponStatus}
        setCouponStatus={setCouponStatus}
        pickupPhotos={pickupPhotos}
        setPickupPhotos={setPickupPhotos}
        pricing={pricing}
        submitting={submitting}
        onSubmit={handleSubmit}
        openPolicy={openPolicy}
        setOpenPolicy={setOpenPolicy}
      />

      {/* ===== POLICY FOOTER ===== */}
      <PolicyFooter />

      {/* Floating "Verified by Stripe" sticker - appears when scrolling near payment area */}
      <StripeVerifiedSticker />

      {/* Floating "Today's bookings" social-proof ticker — anonymized recent registrations */}
      <SocialProofTicker />

      {/* Sticky mobile register CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 px-4 py-3 shadow-2xl" style={{ borderColor: C.purple }}>
        <button onClick={() => scrollTo("register")} className="w-full py-3 rounded-full font-extrabold text-white" style={{ backgroundColor: C.purple }} data-testid="sticky-register-btn">
          Register Now — ${earlyBird ? settings.price_early_bird : settings.price_regular}/child · 3 days
        </button>
      </div>
    </div>
  );
};

/* ============================================================
   Spots Remaining Live Counter (hero scarcity signal)
   - Live from /api/public/market-camp/spots
   - Shows mini progress bars per age group
   - Pulses on critically low remaining
   ============================================================ */
const SpotsRemainingHero = ({ spots }) => {
  if (!spots || !spots.groups || spots.groups.length === 0) return null;
  const groupColor = { "4-7": "#E91E80", "8-11": "#F2762E", "12-16": "#0FA9A0" };
  const totalRemaining = spots.total_remaining ?? 0;
  const totalCap = spots.total_capacity || 1;
  const pctRemaining = Math.round((totalRemaining / totalCap) * 100);
  const lowOverall = pctRemaining <= 35;
  const bannerText = spots.banner_text && spots.banner_text.trim()
    ? spots.banner_text
    : `Live · ${totalRemaining} spot${totalRemaining === 1 ? "" : "s"} left across all groups`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="mt-5 rounded-2xl p-3.5 backdrop-blur"
      style={{
        background: "rgba(255,255,255,0.08)",
        border: lowOverall ? "1.5px solid rgba(245,197,24,0.9)" : "1px solid rgba(255,255,255,0.18)",
      }}
      data-testid="spots-remaining-hero"
    >
      <div className="flex items-center gap-2 mb-2.5">
        <span className="relative flex h-2.5 w-2.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: lowOverall ? "#F5C518" : "#3FA437" }} />
          <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ backgroundColor: lowOverall ? "#F5C518" : "#3FA437" }} />
        </span>
        <p className="text-[10px] uppercase tracking-[2px] font-extrabold text-white/85">
          {bannerText}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {spots.groups.map((g) => {
          const color = groupColor[g.key] || "#F5C518";
          const remaining = g.remaining;
          const cap = g.capacity || 1;
          const pct = Math.max(0, Math.min(100, Math.round(((cap - remaining) / cap) * 100)));
          const low = remaining <= 4 && remaining > 0;
          const full = remaining === 0;
          return (
            <div key={g.key} className="space-y-1" data-testid={`spots-group-${g.key}`}>
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] uppercase tracking-wider font-bold" style={{ color }}>{g.title}</span>
                <span className={`text-[10px] font-black ${full ? "text-red-400" : low ? "text-yellow-300" : "text-white"}`}>
                  {full ? "Full" : `${remaining} left`}
                </span>
              </div>
              <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.15)" }}>
                <div
                  className="h-full rounded-full transition-all duration-700"
                  style={{ width: `${pct}%`, background: full ? "#EF4444" : color }}
                />
              </div>
              <p className="text-[9px] text-white/55">{g.age_label || `Ages ${g.key.replace("-", "–")}`}</p>
            </div>
          );
        })}
      </div>
    </motion.div>
  );
};

/* ============================================================
   Social Proof Ticker — anonymized recent bookings (bottom-left)
   - Cycles through items every ~5s with fade/slide animation
   - Pulls from /public/market-camp/social-proof
   - Hides itself when no items or disabled by admin
   ============================================================ */
const SocialProofTicker = () => {
  const [items, setItems] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [rotationMs, setRotationMs] = useState(11000);
  const [idx, setIdx] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/market-camp/social-proof`).then((r) => {
      if (cancelled) return;
      setEnabled(!!r.data?.enabled);
      setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      if (r.data?.rotation_ms && Number(r.data.rotation_ms) > 1000) {
        setRotationMs(Number(r.data.rotation_ms));
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), rotationMs);
    return () => clearInterval(t);
  }, [items.length, rotationMs]);

  if (!enabled || items.length === 0 || !visible) return null;
  const current = items[idx % items.length];

  return (
    <div
      className="fixed left-3 sm:left-6 bottom-4 sm:bottom-6 z-40 pb-16 md:pb-0 pointer-events-none"
      data-testid="social-proof-ticker"
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, x: -20, scale: 0.92 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          exit={{ opacity: 0, x: -20, scale: 0.92 }}
          transition={{ type: "spring", stiffness: 300, damping: 24 }}
          className="pointer-events-auto rounded-2xl shadow-2xl flex items-center gap-3 pl-3 pr-4 py-2.5 backdrop-blur max-w-[320px] sm:max-w-[380px]"
          style={{
            background: "rgba(255,255,255,0.97)",
            border: `1.5px solid ${C.green}40`,
          }}
        >
          {/* Avatar circle with first initial */}
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-white font-black text-base shadow-sm"
            style={{ background: `linear-gradient(135deg, ${C.pink} 0%, ${C.orange} 100%)` }}
          >
            {(current.text || "?").trim().charAt(0).toUpperCase()}
          </div>
          {/* Text */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: C.green }} />
                <span className="relative inline-flex rounded-full h-2 w-2" style={{ backgroundColor: C.green }} />
              </span>
              <p className="text-[9px] uppercase tracking-[1.5px] font-extrabold text-gray-500 leading-none">Just registered</p>
            </div>
            <p className="text-[12px] sm:text-[13px] font-bold text-gray-900 leading-tight truncate">{current.text}</p>
            {current.ago_label && (
              <p className="text-[10px] text-gray-500 leading-tight mt-0.5">{current.ago_label}</p>
            )}
          </div>
          {/* Dismiss */}
          <button
            onClick={() => setVisible(false)}
            className="text-gray-300 hover:text-gray-700 transition-colors text-lg leading-none flex-shrink-0"
            aria-label="Dismiss"
            data-testid="dismiss-social-proof"
          >
            ×
          </button>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

/* ============================================================
   Floating "Verified by Stripe" Trust Sticker
   - Appears with animation when user scrolls near registration form
   - Dismissible (state persists for session only)
   ============================================================ */
const StripeVerifiedSticker = () => {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const formEl = document.getElementById("register");
    if (!formEl) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Show as soon as 5% of form section is intersecting (user is scrolling toward it)
          if (entry.isIntersecting && entry.intersectionRatio > 0.02) {
            setVisible(true);
          }
        });
      },
      { threshold: [0, 0.02, 0.1] }
    );
    observer.observe(formEl);
    return () => observer.disconnect();
  }, []);

  if (dismissed || !visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.85 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 30, scale: 0.85 }}
        transition={{ type: "spring", stiffness: 320, damping: 24 }}
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 pb-16 md:pb-0"
        data-testid="stripe-verified-sticker"
      >
        <div
          className="relative rounded-2xl shadow-2xl overflow-hidden flex items-center gap-3 pl-3 pr-4 py-3 backdrop-blur"
          style={{
            background: "linear-gradient(135deg, rgba(11,39,72,0.97) 0%, rgba(31,78,138,0.97) 100%)",
            border: "1px solid rgba(255,255,255,0.18)",
            minWidth: "240px",
          }}
        >
          {/* Animated pulse ring */}
          <span className="absolute -top-1 -right-1 flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75" style={{ backgroundColor: "#3FA437" }} />
            <span className="relative inline-flex rounded-full h-3 w-3" style={{ backgroundColor: "#3FA437" }} />
          </span>

          {/* Shield icon */}
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: "rgba(255,255,255,0.12)" }}
          >
            <ShieldCheck className="w-6 h-6 text-white" strokeWidth={2.5} />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p className="text-[9px] uppercase tracking-[2px] text-white/60 font-bold leading-none">Payments verified by</p>
            <p className="text-sm font-black text-white leading-tight mt-0.5 flex items-center gap-1.5">
              <span style={{ color: "#A5B4FC" }} className="text-base tracking-tight">stripe</span>
              <span className="text-[10px] text-white/70 font-bold">·</span>
              <span className="text-[10px] text-white/80 font-bold">256-bit SSL</span>
            </p>
          </div>

          {/* Dismiss */}
          <button
            onClick={() => setDismissed(true)}
            className="text-white/40 hover:text-white/90 transition-colors text-lg leading-none ml-1 flex-shrink-0"
            aria-label="Dismiss"
            data-testid="dismiss-stripe-sticker"
          >
            ×
          </button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

/* ============================================================
   Registration Form Component
   ============================================================ */
const RegistrationForm = ({
  id, settings, numChildren, setNumChildren, children, updateChild,
  parent, setParent, consents, setConsents, whatsappOptIn, setWhatsappOptIn,
  referralCode, setReferralCode, referralStatus, setReferralStatus,
  couponCode, setCouponCode, couponStatus, setCouponStatus,
  pickupPhotos, setPickupPhotos,
  pricing, submitting, onSubmit,
}) => {
  const handleParentChange = (e) => {
    const { name, value } = e.target;
    setParent(p => ({ ...p, [name]: value }));
  };
  const toggleConsent = (key) => setConsents(c => ({ ...c, [key]: !c[key] }));

  // Debounced referral code validation
  useEffect(() => {
    const code = (referralCode || "").trim().toUpperCase();
    if (!code) {
      setReferralStatus({ state: "idle", message: "" });
      return;
    }
    if (code.length < 4) {
      setReferralStatus({ state: "idle", message: "" });
      return;
    }
    setReferralStatus({ state: "checking", message: "Checking…" });
    const t = setTimeout(() => {
      axios.post(`${API}/public/market-camp/validate-referral`, { code, email: parent.parent_email || "" })
        .then((r) => {
          if (r.data?.valid) {
            setReferralStatus({
              state: "valid",
              message: `✓ Valid! ${r.data.referrer_first_name} will receive $${r.data.credit_amount?.toFixed?.(0) || r.data.credit_amount} credit.`,
            });
          } else {
            const reason = r.data?.reason || "invalid";
            const msg = reason === "self_referral" ? "You can't use your own code."
              : reason === "not_found" ? "Code not recognized."
              : reason === "disabled" ? "Referrals are currently disabled."
              : "Invalid code.";
            setReferralStatus({ state: "invalid", message: msg });
          }
        })
        .catch(() => setReferralStatus({ state: "invalid", message: "Could not verify code." }));
    }, 400);
    return () => clearTimeout(t);
  }, [referralCode, parent.parent_email, setReferralStatus]);

  // Debounced coupon code validation
  useEffect(() => {
    const code = (couponCode || "").trim().toUpperCase();
    if (!code) {
      setCouponStatus({ state: "idle", message: "", discount_amount: 0, discount_label: "", description: "" });
      return;
    }
    if (code.length < 3) {
      setCouponStatus({ state: "idle", message: "", discount_amount: 0, discount_label: "", description: "" });
      return;
    }
    setCouponStatus(s => ({ ...s, state: "checking", message: "Checking…" }));
    const t = setTimeout(() => {
      axios.post(`${API}/public/market-camp/validate-coupon`, { code, num_children: numChildren })
        .then((r) => {
          if (r.data?.valid) {
            setCouponStatus({
              state: "valid",
              message: `✓ ${r.data.discount_label} applied — Save $${r.data.discount_amount?.toFixed(2)}`,
              discount_amount: r.data.discount_amount || 0,
              discount_label: r.data.discount_label || "",
              description: r.data.description || "",
            });
          } else {
            setCouponStatus({
              state: "invalid",
              message: r.data?.message || "Invalid code.",
              discount_amount: 0, discount_label: "", description: "",
            });
          }
        })
        .catch(() => setCouponStatus({
          state: "invalid", message: "Could not verify code.",
          discount_amount: 0, discount_label: "", description: "",
        }));
    }, 400);
    return () => clearTimeout(t);
  }, [couponCode, numChildren, setCouponStatus]);

  if (!pricing) return null;

  return (
    <section id={id} className="py-16 px-6" style={{ backgroundColor: C.cream }} data-testid="registration-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <span className="inline-block px-5 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider mb-4 text-white" style={{ backgroundColor: C.purple }}>
            Register Your Child
          </span>
          <h2 className="text-3xl sm:text-4xl font-black" style={{ color: C.purpleDeep }}>Reserve your spot today.</h2>
          <p className="text-gray-600 mt-2 text-sm">Full payment required to secure your child's place. Limited spots available.</p>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-3xl p-5 sm:p-8 shadow-lg space-y-7" style={{ border: `2px solid ${C.purple}15` }} data-testid="registration-form">
          {/* PARENT */}
          <FormSection icon={Users} title="Parent / Guardian" color={C.purple}>
            <div className="grid sm:grid-cols-2 gap-4">
              <InputField label="Parent / Guardian Full Name *" name="parent_full_name" value={parent.parent_full_name} onChange={handleParentChange} required testid="parent-name" />
              <InputField label="Email Address *" name="parent_email" type="email" value={parent.parent_email} onChange={handleParentChange} required testid="parent-email" />
              <InputField label="Phone Number *" name="parent_phone" type="tel" value={parent.parent_phone} onChange={handleParentChange} required testid="parent-phone" />
              <InputField label="Authorized Pickup Persons *" name="authorized_pickup" placeholder="Names, separated by commas" value={parent.authorized_pickup} onChange={handleParentChange} required testid="authorized-pickup" />
            </div>
          </FormSection>

          {/* EMERGENCY */}
          <FormSection icon={AlertTriangle} title="Emergency Contacts" color={C.orange}>
            <div className="grid sm:grid-cols-2 gap-4">
              <InputField label="Emergency Contact Name *" name="emergency_contact_name" value={parent.emergency_contact_name} onChange={handleParentChange} required testid="emergency-name" />
              <InputField label="Emergency Contact Phone *" name="emergency_contact_phone" value={parent.emergency_contact_phone} onChange={handleParentChange} required testid="emergency-phone" />
              <InputField label="Secondary Emergency Name" name="secondary_emergency_name" value={parent.secondary_emergency_name} onChange={handleParentChange} testid="emergency-name-2" />
              <InputField label="Secondary Emergency Phone" name="secondary_emergency_phone" value={parent.secondary_emergency_phone} onChange={handleParentChange} testid="emergency-phone-2" />
            </div>
          </FormSection>

          {/* PICKUP VERIFICATION (safety) */}
          <div id="pickup-verification-anchor" />
          <FormSection icon={Shield} title="Pickup Verification (Safety) — Required" color={C.purpleDeep}>
            <div className="rounded-xl p-3 mb-4 flex items-start gap-2.5" style={{ background: `${C.purple}10`, border: `2px solid ${C.purple}60` }}>
              <Shield className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: C.purpleDeep }} strokeWidth={2.25} />
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold uppercase tracking-widest" data-testid="pickup-required-badge">
                    Required
                  </span>
                  <p className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: C.purpleDeep }}>Both photos must be uploaded to complete registration</p>
                </div>
                <p className="text-[12px] text-gray-800 leading-snug">
                  <strong style={{ color: C.purpleDeep }}>For your child&apos;s safety</strong>, we require a photo
                  of the pickup person&apos;s government-issued ID and a <strong>matching selfie</strong>.
                  At pickup, our staff will verify the photos against the person collecting your child.
                  Photos are stored securely and only viewed by camp staff.
                </p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <PickupPhotoUpload
                kind="id"
                label="Government-Issued Photo ID *"
                hint="Driver's license, passport, or provincial ID. Name must match an authorized pickup person above."
                photo={pickupPhotos.id}
                onChange={(next) => setPickupPhotos(p => ({ ...p, id: next }))}
              />
              <PickupPhotoUpload
                kind="selfie"
                label="Selfie of Pickup Person *"
                hint="Clear face photo of the SAME person whose ID you uploaded. We'll match these at pickup."
                photo={pickupPhotos.selfie}
                onChange={(next) => setPickupPhotos(p => ({ ...p, selfie: next }))}
              />
            </div>
            <p className="mt-3 text-[10px] text-gray-500">
              JPEG, PNG, WebP, or HEIC · Max 8 MB each · Captured live on phone if possible.
            </p>
          </FormSection>

          {/* CHILDREN COUNT */}
          <FormSection icon={Sparkles} title="Children to Register" color={C.pink}>
            <label className="block text-xs font-extrabold text-gray-700 mb-1.5 uppercase tracking-wider">How many children? *</label>
            <select
              value={numChildren}
              onChange={(e) => setNumChildren(parseInt(e.target.value, 10))}
              className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-white text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all font-semibold"
              data-testid="num-children-select"
            >
              {[1, 2, 3, 4].map(n => {
                const lbl =
                  n === 1 ? "1 child" :
                  n === 2 ? `2 children · ${settings.sibling_discount_2}% sibling discount` :
                  n === 3 ? `3 children · ${settings.sibling_discount_3}% family discount` :
                  `4 children · ${settings.sibling_discount_4}% family discount`;
                return <option key={n} value={n}>{lbl}</option>;
              })}
            </select>
            <p className="mt-2 text-xs text-gray-500">Have 5+ children? <a href="/contact" className="font-bold underline" style={{ color: C.purple }}>Contact us</a> for a custom family rate.</p>
          </FormSection>

          {/* CHILDREN DETAILS */}
          {children.map((child, i) => (
            <ChildBlock key={i} index={i} child={child} updateChild={updateChild} />
          ))}

          {/* WHATSAPP */}
          <div className="rounded-2xl p-4 flex items-start gap-3" style={{ backgroundColor: "#25D36615", border: "2px solid #25D36640" }}>
            <input
              type="checkbox" checked={whatsappOptIn} onChange={() => setWhatsappOptIn(v => !v)}
              className="mt-1 w-5 h-5 rounded" style={{ accentColor: "#25D366" }}
              data-testid="whatsapp-optin"
            />
            <div>
              <p className="font-bold text-gray-900 text-sm flex items-center gap-2">
                <MessageCircle className="w-4 h-4" style={{ color: "#25D366" }} /> Send my payment confirmation via WhatsApp
              </p>
              <p className="text-xs text-gray-600 mt-0.5">After payment, you'll get a WhatsApp link to message us directly with your registration code.</p>
            </div>
          </div>

          {/* REFERRAL CODE (optional) */}
          {settings?.referral_enabled !== false && (
            <div
              className="rounded-2xl p-4"
              style={{ background: "linear-gradient(135deg, #FFF7E6 0%, #FFEFC2 100%)", border: `2px solid ${C.gold}80` }}
              data-testid="referral-code-section"
            >
              <div className="flex items-start gap-3 mb-2">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: C.gold, color: C.purpleDeep }}
                >
                  <TagIcon className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-extrabold text-sm" style={{ color: C.purpleDeep }}>
                    Got a referral code? <span className="font-normal text-gray-600">(Optional)</span>
                  </p>
                  <p className="text-[11px] text-gray-700 leading-tight">
                    A friend shared their code? Drop it below — they get a <strong>${(settings?.referral_credit_amount ?? 20)} thank-you credit</strong>.
                  </p>
                </div>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                  placeholder="e.g. A1B2C3"
                  maxLength={12}
                  className="w-full px-4 py-3 pr-28 rounded-xl border-2 bg-white text-gray-900 font-mono font-bold tracking-[3px] uppercase placeholder:font-sans placeholder:tracking-normal placeholder:text-gray-400 focus:ring-2 outline-none transition-all"
                  style={{
                    borderColor:
                      referralStatus.state === "valid" ? C.green :
                      referralStatus.state === "invalid" ? "#EF4444" :
                      `${C.purple}40`,
                  }}
                  data-testid="referral-code-input"
                />
                {referralStatus.state === "checking" && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-gray-300 border-t-[#5B2A86] rounded-full animate-spin" />
                )}
                {referralStatus.state === "valid" && (
                  <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: C.green }} strokeWidth={2.5} />
                )}
                {referralStatus.state === "invalid" && (
                  <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" strokeWidth={2.5} />
                )}
              </div>
              {referralStatus.message && (
                <p
                  className="mt-2 text-xs font-bold"
                  style={{
                    color:
                      referralStatus.state === "valid" ? C.green :
                      referralStatus.state === "invalid" ? "#DC2626" :
                      "#6B7280",
                  }}
                  data-testid="referral-status-msg"
                >
                  {referralStatus.message}
                </p>
              )}
              {/* SELL-THE-REFERRED — show only when a valid code is entered, encouraging them to refer too */}
              {referralStatus.state === "valid" && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  className="mt-3 rounded-xl p-3 flex items-start gap-2.5"
                  style={{ background: "rgba(91,42,134,0.10)", border: `1.5px dashed ${C.purple}80` }}
                  data-testid="sell-referred-banner"
                >
                  <Sparkles className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: C.purple }} strokeWidth={2.5} />
                  <p className="text-[11px] text-gray-800 leading-snug">
                    <strong style={{ color: C.purpleDeep }}>You can earn ${(settings?.referral_credit_amount ?? 20)} too!</strong> After you register,
                    we&apos;ll give you your <strong>own unique share code</strong> on the confirmation page.
                    Share it with friends — every friend who registers earns you <strong>${(settings?.referral_credit_amount ?? 20)} thank-you credit</strong>.
                    <span className="block mt-1 text-[10px] text-gray-600">No limits — refer as many friends as you like.</span>
                  </p>
                </motion.div>
              )}
            </div>
          )}

          {/* COUPON / DISCOUNT CODE (optional) */}
          <div
            className="rounded-2xl p-4"
            style={{ background: "linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)", border: "2px solid #10B98180" }}
            data-testid="coupon-code-section"
          >
            <div className="flex items-start gap-3 mb-2">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: "#10B981", color: "white" }}
              >
                <TagIcon className="w-5 h-5" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-sm" style={{ color: "#065F46" }}>
                  Have a discount code? <span className="font-normal text-gray-600">(Optional)</span>
                </p>
                <p className="text-[11px] text-gray-700 leading-tight">
                  Got a promo or early-bird code from us? Enter it here — discount applies instantly.
                </p>
              </div>
            </div>
            <div className="relative">
              <input
                type="text"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="e.g. EARLYBIRD20"
                maxLength={32}
                className="w-full px-4 py-3 pr-28 rounded-xl border-2 bg-white text-gray-900 font-mono font-bold tracking-[2px] uppercase placeholder:font-sans placeholder:tracking-normal placeholder:text-gray-400 focus:ring-2 outline-none transition-all"
                style={{
                  borderColor:
                    couponStatus.state === "valid" ? C.green :
                    couponStatus.state === "invalid" ? "#EF4444" :
                    "#10B98140",
                }}
                data-testid="coupon-code-input"
              />
              {couponStatus.state === "checking" && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-gray-300 border-t-[#065F46] rounded-full animate-spin" />
              )}
              {couponStatus.state === "valid" && (
                <CheckCircle2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: C.green }} strokeWidth={2.5} />
              )}
              {couponStatus.state === "invalid" && (
                <AlertTriangle className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-red-500" strokeWidth={2.5} />
              )}
            </div>
            {couponStatus.message && (
              <p
                className="mt-2 text-xs font-bold"
                style={{
                  color:
                    couponStatus.state === "valid" ? C.green :
                    couponStatus.state === "invalid" ? "#DC2626" :
                    "#6B7280",
                }}
                data-testid="coupon-status-msg"
              >
                {couponStatus.message}
              </p>
            )}
            {couponStatus.state === "valid" && couponStatus.description && (
              <p className="mt-1 text-[11px] text-gray-600 italic">{couponStatus.description}</p>
            )}
          </div>

          {/* PRICING SUMMARY */}
          <PricingSummary settings={settings} numChildren={numChildren} pricing={pricing} couponStatus={couponStatus} />

          {/* CONSENTS */}
          <FormSection icon={Lock} title="Required Consents (all 10 required)" color={C.purpleDeep}>
            <div className="space-y-2.5">
              {[
                ["consent_legal_guardian", "I confirm that I am the child's parent or legal guardian and I have authority to register this child."],
                ["consent_info_accurate", "I confirm that all medical, allergy, dietary, behavioral, and emergency information provided is complete and accurate."],
                ["consent_food_allergy_disclosed", "I understand that food, snacks, and drinks will be served and I have disclosed all allergies, intolerances, dietary restrictions, and medical food-related needs."],
                ["consent_emergency_medical", "I authorize the organizers to seek emergency medical assistance for my child if necessary."],
                ["consent_authorized_pickup", "I understand that my child must be picked up only by authorized individuals unless written approval is given."],
                ["consent_showcase_attendance", "I understand and agree that parents/guardians are expected to attend the final day product Showcase to support their child."],
                ["consent_full_payment", "I understand that full payment is required to reserve my child's spot."],
                ["consent_policies_waiver", "I have read and agree to the camp policies, the NO-REFUND POLICY (registration is non-refundable due to material commitments — transfers to another family are allowed), conduct policy, pickup policy, media consent terms, privacy terms, and participation waiver.", { linkTo: "/mini-makers-market-camp/policies", linkLabel: "Read full policies →" }],
                ["consent_hands_on_activities", "I understand that program activities may involve hands-on crafting, pouring, mixing, packaging, scenting, and supervised use of materials."],
                ["consent_organizers_authority", "I understand that organizers may refuse participation, remove a child from an activity, or contact a parent/guardian where health, safety, conduct, or supervision concerns arise."],
              ].map((row, idx) => {
                const [key, label, extra] = row;
                return (
                <label key={key} className="flex items-start gap-3 p-3 rounded-xl bg-[#FAF6EC] hover:bg-[#F5EFD9] cursor-pointer transition-colors" data-testid={`consent-${key}`}>
                  <input
                    type="checkbox" checked={consents[key]} onChange={() => toggleConsent(key)}
                    className="mt-1 w-5 h-5 rounded flex-shrink-0" style={{ accentColor: C.purple }}
                  />
                  <span className="text-xs sm:text-sm text-gray-800 leading-relaxed">
                    <span className="font-bold mr-1.5" style={{ color: C.purple }}>{idx + 1}.</span>{label}
                    {extra?.linkTo && (
                      <a
                        href={extra.linkTo}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded-full font-bold text-[11px] hover:underline"
                        style={{ background: `${C.purple}15`, color: C.purple }}
                        data-testid={`consent-link-${key}`}
                      >
                        {extra.linkLabel}
                      </a>
                    )}
                  </span>
                </label>
                );
              })}
            </div>
          </FormSection>

          {/* SECURE CHECKOUT PANEL — final trust signals right before payment */}
          <SecureCheckoutPanel />

          {/* SUBMIT */}
          {(() => {
            const finalTotal = Math.max(0, pricing.total - (couponStatus?.state === "valid" ? (couponStatus.discount_amount || 0) : 0));
            const isFree = finalTotal <= 0 && couponStatus?.state === "valid";
            return (
              <button
                type="submit" disabled={submitting}
                className="w-full py-4 rounded-2xl font-extrabold text-base sm:text-lg text-white shadow-xl hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed relative overflow-hidden"
                style={{ background: isFree ? `linear-gradient(135deg, ${C.green} 0%, #2D7D26 100%)` : `linear-gradient(135deg, ${C.purple} 0%, ${C.purpleDeep} 100%)` }}
                data-testid="submit-checkout-btn"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    {isFree ? "Confirming your free registration…" : "Creating secure checkout…"}
                  </span>
                ) : isFree ? (
                  <span className="flex items-center justify-center gap-2.5" data-testid="submit-checkout-free">
                    <CheckCircle2 className="w-5 h-5" strokeWidth={2.5} />
                    <span>Complete Free Registration · $0.00 CAD</span>
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2.5">
                    <Lock className="w-5 h-5" strokeWidth={2.5} />
                    <span>Continue to Secure Payment · ${finalTotal.toFixed(2)} CAD</span>
                  </span>
                )}
              </button>
            );
          })()}

          {/* Encryption strip directly below button */}
          <div className="flex items-center justify-center gap-4 flex-wrap text-[11px] sm:text-xs text-gray-600 -mt-1">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: C.green }} strokeWidth={2.5} />
              <span className="font-bold">256-bit TLS encrypted</span>
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span className="flex items-center gap-1.5">
              <EyeOff className="w-3.5 h-3.5" style={{ color: C.green }} strokeWidth={2.5} />
              <span className="font-bold">We never see your card</span>
            </span>
            <span className="w-1 h-1 rounded-full bg-gray-300" />
            <span className="flex items-center gap-1.5">
              <Fingerprint className="w-3.5 h-3.5" style={{ color: C.green }} strokeWidth={2.5} />
              <span className="font-bold">PCI-DSS Level 1</span>
            </span>
          </div>
        </form>
      </div>
    </section>
  );
};

/* ============================================================
   Secure Checkout Panel — visible trust signals before submit
   ============================================================ */
const SecureCheckoutPanel = () => (
  <div
    className="rounded-2xl overflow-hidden border-2"
    style={{ borderColor: "#1F8BD940", background: "linear-gradient(180deg, #F8FBFF 0%, #FFFFFF 100%)" }}
    data-testid="secure-checkout-panel"
  >
    {/* Header */}
    <div
      className="px-5 py-3 flex items-center justify-between border-b"
      style={{ borderColor: "#1F8BD922", background: "linear-gradient(90deg, #0B2748 0%, #1F4E8A 100%)" }}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
          <ShieldCheck className="w-5 h-5 text-white" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[2px] text-white/70 font-bold leading-none">Secure checkout</p>
          <p className="text-sm font-extrabold text-white leading-tight mt-0.5">Powered by Stripe · 256-bit Encrypted</p>
        </div>
      </div>
      <span className="hidden sm:flex items-center gap-1 text-[10px] font-bold text-white/90 uppercase tracking-wider">
        <Lock className="w-3 h-3" /> https
      </span>
    </div>

    {/* Trust grid */}
    <div className="px-5 py-4">
      <div className="grid grid-cols-2 gap-2.5">
        <TrustChip icon={KeyRound} title="256-bit SSL/TLS" subtitle="Encrypted in transit" />
        <TrustChip icon={Fingerprint} title="PCI-DSS Level 1" subtitle="Highest card standard" />
        <TrustChip icon={EyeOff} title="No card stored" subtitle="We never see your card" />
        <TrustChip icon={Shield} title="3D Secure / SCA" subtitle="Bank-grade verification" />
      </div>

      {/* Card brands */}
      <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-1.5">
          <CardBrand label="VISA" bg="#1A1F71" />
          <CardBrand label="MC" bg="#EB001B" accent="#F79E1B" />
          <CardBrand label="AMEX" bg="#2E77BC" />
          <CardBrand label="DISC" bg="#FF6000" />
          <CardBrand label="Pay" bg="#000000" />
          <CardBrand label="G Pay" bg="#4285F4" />
        </div>
        <StripeBadge />
      </div>

      {/* Footer reassurance */}
      <div className="mt-4 pt-3 border-t border-gray-200 flex items-start gap-2">
        <CreditCard className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "#1F4E8A" }} strokeWidth={2.5} />
        <p className="text-[11px] sm:text-xs text-gray-700 leading-relaxed">
          You'll be redirected to <span className="font-extrabold text-gray-900">Stripe's hosted checkout</span> at
          <span className="font-mono font-bold mx-1 text-[10px] px-1.5 py-0.5 bg-gray-100 rounded" style={{ color: "#1F4E8A" }}>checkout.stripe.com</span>
          to securely enter your card. Your details are <span className="font-bold">never sent to or stored on our servers</span>.
        </p>
      </div>
    </div>
  </div>
);

const TrustChip = ({ icon: Icon, title, subtitle }) => (
  <div
    className="flex items-start gap-2.5 p-3 rounded-xl bg-white border"
    style={{ borderColor: "#1F8BD922" }}
  >
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor: "#1F4E8A12" }}
    >
      <Icon className="w-4 h-4" style={{ color: "#1F4E8A" }} strokeWidth={2.5} />
    </div>
    <div className="min-w-0">
      <p className="font-extrabold text-[12px] sm:text-[13px] text-gray-900 leading-tight">{title}</p>
      <p className="text-[10px] sm:text-[11px] text-gray-500 leading-tight mt-0.5">{subtitle}</p>
    </div>
  </div>
);

const CardBrand = ({ label, bg, accent }) => (
  <div
    className="h-6 px-1.5 rounded-[5px] flex items-center justify-center text-[9px] font-black text-white tracking-wide shadow-sm relative overflow-hidden"
    style={{ backgroundColor: bg, minWidth: "32px" }}
    title={label}
  >
    {accent && (
      <span
        className="absolute right-0 top-0 bottom-0 w-1/2 opacity-90"
        style={{ backgroundColor: accent }}
      />
    )}
    <span className="relative z-10">{label}</span>
  </div>
);

const StripeBadge = () => (
  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg border bg-white" style={{ borderColor: "#635BFF40" }}>
    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Powered by</span>
    <span className="text-sm font-black tracking-tight" style={{ color: "#635BFF", fontFamily: "system-ui" }}>stripe</span>
  </div>
);


/* Pricing breakdown card */
const PricingSummary = ({ settings, numChildren, pricing, couponStatus }) => {
  const couponDiscount = (couponStatus?.state === "valid") ? (couponStatus.discount_amount || 0) : 0;
  const grandTotal = Math.max(0, pricing.total - couponDiscount);
  return (
    <div className="rounded-2xl p-5" style={{ backgroundColor: C.purpleDeep, color: "white" }} data-testid="pricing-summary">
      <p className="text-xs uppercase tracking-widest font-bold mb-3" style={{ color: C.gold }}>
        Your Total
      </p>
      <div className="space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="opacity-80">{numChildren} × ${pricing.per.toFixed(2)}</span><span>${pricing.subtotal.toFixed(2)}</span></div>
        {pricing.pct > 0 && (
          <div className="flex justify-between" style={{ color: C.gold }}>
            <span>Sibling discount ({pricing.pct}%)</span><span>−${pricing.discountAmount.toFixed(2)}</span>
          </div>
        )}
        {couponDiscount > 0 && (
          <div className="flex justify-between text-[#7BFFA6]" data-testid="coupon-line">
            <span>Coupon {couponStatus?.discount_label ? `· ${couponStatus.discount_label}` : ""}</span>
            <span>−${couponDiscount.toFixed(2)}</span>
          </div>
        )}
        <div className="border-t border-white/20 pt-2 flex justify-between text-lg font-extrabold">
          <span>Total</span><span>${grandTotal.toFixed(2)} CAD</span>
        </div>
        {grandTotal <= 0 && couponDiscount > 0 && (
          <p className="text-xs mt-2 px-2 py-1.5 rounded-md font-bold text-center" style={{ background: "rgba(123,255,166,0.15)", color: "#7BFFA6" }} data-testid="pricing-free-banner">
            🎉 100% off — no payment required. Click "Complete Free Registration" below.
          </p>
        )}
        {settings.early_bird_active && (
          <p className="text-xs mt-2" style={{ color: C.gold }}>★ Early bird pricing active</p>
        )}
      </div>
    </div>
  );
};

/* Section wrapper */
const FormSection = ({ icon: Icon, title, color, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}18` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <h3 className="font-extrabold text-sm sm:text-base" style={{ color }}>{title}</h3>
    </div>
    {children}
  </div>
);

const InputField = ({ label, name, value, onChange, type = "text", required, placeholder, testid }) => (
  <div>
    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
    <input
      type={type} name={name} value={value} onChange={onChange} required={required} placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-[#FAF6EC] text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all placeholder:text-gray-400"
      data-testid={testid}
    />
  </div>
);

const TextArea = ({ label, name, value, onChange, placeholder, testid, rows = 2 }) => (
  <div>
    <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
    <textarea
      name={name} value={value} onChange={onChange} rows={rows} placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-[#FAF6EC] text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all resize-none placeholder:text-gray-400 text-sm"
      data-testid={testid}
    />
  </div>
);

const PickupPhotoUpload = ({ kind, label, hint, photo, onChange }) => {
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error("File too large — max 8 MB");
      return;
    }
    onChange({ ...photo, uploading: true, preview: URL.createObjectURL(file) });
    const fd = new FormData();
    fd.append("file", file);
    fd.append("photo_type", kind);
    try {
      const r = await axios.post(`${API}/public/market-camp/pickup-photo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange({
        path: r.data.storage_path,
        content_type: r.data.content_type,
        preview: URL.createObjectURL(file),
        uploading: false,
      });
      toast.success(kind === "id" ? "ID photo uploaded" : "Selfie uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
      onChange({ path: "", content_type: "", preview: "", uploading: false });
    }
  };

  const clear = () => {
    onChange({ path: "", content_type: "", preview: "", uploading: false });
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div data-testid={`pickup-photo-${kind}`}>
      <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">{label}</label>
      {!photo.path && !photo.uploading ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full aspect-[4/3] rounded-xl border-2 border-dashed flex flex-col items-center justify-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
          style={{ borderColor: `${C.purple}40`, background: `${C.purple}05` }}
        >
          {kind === "id" ? (
            <CreditCard className="w-8 h-8" style={{ color: C.purple }} strokeWidth={1.5} />
          ) : (
            <User className="w-8 h-8" style={{ color: C.purple }} strokeWidth={1.5} />
          )}
          <p className="text-xs font-bold" style={{ color: C.purpleDeep }}>
            Tap to upload {kind === "id" ? "photo ID" : "selfie"}
          </p>
          <p className="text-[10px] text-gray-500 px-3 text-center">{hint}</p>
        </button>
      ) : (
        <div className="relative rounded-xl overflow-hidden border-2 aspect-[4/3]" style={{ borderColor: C.green }}>
          {photo.preview && (
            <img src={photo.preview} alt={`Uploaded ${kind}`} className="w-full h-full object-cover" />
          )}
          {photo.uploading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-white text-sm font-bold">
              <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Uploading…
            </div>
          ) : (
            <>
              <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider text-white flex items-center gap-1" style={{ background: C.green }}>
                <CheckCircle2 className="w-3 h-3" strokeWidth={3} /> Uploaded
              </div>
              <button
                type="button"
                onClick={clear}
                className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-bold text-white"
                style={{ background: "rgba(0,0,0,0.55)" }}
              >
                Replace
              </button>
            </>
          )}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/heic,image/heif,image/*"
        capture={kind === "selfie" ? "user" : "environment"}
        onChange={handleFile}
        className="hidden"
        data-testid={`pickup-input-${kind}`}
      />
    </div>
  );
};

const ChildBlock = ({ index, child, updateChild }) => {
  const change = (field) => (e) => updateChild(index, field, e.target.value);
  const togglePhoto = () => updateChild(index, "photo_consent", !child.photo_consent);
  return (
    <div className="rounded-2xl p-5 border-2" style={{ borderColor: `${C.pink}25`, backgroundColor: "#fff" }} data-testid={`child-block-${index}`}>
      <h4 className="font-extrabold text-base mb-3 flex items-center gap-2" style={{ color: C.pink }}>
        <Award className="w-5 h-5" /> Child {index + 1} Details
      </h4>
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <InputField label="Full Name *" name="full_name" value={child.full_name} onChange={change("full_name")} required testid={`child-${index}-name`} />
        <div>
          <label className="block text-xs font-bold text-gray-700 mb-1.5 uppercase tracking-wider">Age *</label>
          <select
            value={child.age} onChange={change("age")} required
            className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 bg-[#FAF6EC] text-gray-900 focus:border-[#5B2A86] focus:ring-2 focus:ring-[#5B2A86]/20 outline-none transition-all"
            data-testid={`child-${index}-age`}
          >
            <option value="">Select age</option>
            {[4,5,6,7,8,9,10,11,12,13,14,15,16].map(a => <option key={a} value={a}>{a} years old</option>)}
          </select>
        </div>
        <InputField label="Date of Birth" name="date_of_birth" type="date" value={child.date_of_birth} onChange={change("date_of_birth")} testid={`child-${index}-dob`} />
      </div>
      {child.age_group && (
        <div className="mb-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-extrabold" style={{ backgroundColor: `${C.teal}18`, color: C.teal }}>
          <Users className="w-3.5 h-3.5" /> Assigned to Group {child.age_group}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3 mb-3">
        <TextArea label="Allergies (food + other)" name="allergies" value={child.allergies} onChange={change("allergies")} placeholder="e.g. peanut, dairy, latex, none" testid={`child-${index}-allergies`} />
        <TextArea label="Dietary Restrictions" name="dietary_restrictions" value={child.dietary_restrictions} onChange={change("dietary_restrictions")} placeholder="e.g. halal, vegetarian, gluten-free, none" testid={`child-${index}-diet`} />
        <TextArea label="Medical Conditions" name="medical_conditions" value={child.medical_conditions} onChange={change("medical_conditions")} placeholder="e.g. asthma, eczema, none" testid={`child-${index}-medical`} />
        <TextArea label="Medications" name="medications" value={child.medications} onChange={change("medications")} placeholder="Including EpiPen, inhaler, etc. or 'none'" testid={`child-${index}-meds`} />
      </div>
      <TextArea label="Sensory / Behavioral / Support Needs" name="support_needs" value={child.support_needs} onChange={change("support_needs")} placeholder="Anything we should know to make camp a great experience" testid={`child-${index}-support`} />
      <label className="mt-3 flex items-start gap-2 cursor-pointer">
        <input type="checkbox" checked={!!child.photo_consent} onChange={togglePhoto} className="mt-1 w-4 h-4 rounded" style={{ accentColor: C.purple }} data-testid={`child-${index}-photo-consent`} />
        <span className="text-xs text-gray-700">
          I consent for photos/video of <strong>{child.full_name || `Child ${index + 1}`}</strong> to be used in Mix &amp; Mingle marketing materials. (Optional)
        </span>
      </label>
    </div>
  );
};

/* ============================================================
   Policy Footer with expandable summaries
   ============================================================ */
const POLICIES = [
  {
    title: "Terms and Conditions",
    body: "By registering, you agree to participate in Mini Makers Market Camp organised by Mix & Mingle Events on the dates listed. Registration is per-child, payment is required in full, and acceptance is subject to availability. Mix & Mingle Events may update camp details (venue, schedule) and will communicate changes promptly. This is a parent-facing summary, not legal advice.",
  },
  {
    title: "Participation Waiver & Assumption of Risk",
    body: "Activities are supervised but hands-on. Minor spills, stains, scented residue, sticky hands, and small messes are part of the experience. Parents/guardians accept normal participation risks associated with children's craft/program activities and authorise emergency care if needed. Mix & Mingle Events is not liable for incidental events except where required by law.",
  },
  {
    title: "No-Refund Policy · Material Commitment",
    body: "Mini Makers Market Camp registrations are STRICTLY NON-REFUNDABLE. Once you register, we immediately purchase craft materials, food, branded packaging, certificates, and supplies tailored to each registered child. These costs cannot be recovered, which is why registration is non-refundable. If your plans change, we are happy to transfer your child's spot to another family at no charge — please contact us as soon as possible. Mix & Mingle Events may cancel or reschedule for safety, venue, or organiser reasons and will offer a credit toward a future event or a refund if cancellation is at our discretion.",
  },
  {
    title: "Pickup & Release Policy",
    body: "Children may only be picked up by individuals listed on your Authorised Pickup list. Photo ID may be required. Late pickup may incur fees and repeated late pickup may affect future participation. If a pickup fails, we contact the listed parents and emergency contacts.",
  },
  {
    title: "Food, Allergy & Medical Disclosure Policy",
    body: "Food, snacks, and drinks are provided. Parents/guardians must fully disclose all allergies, intolerances, dietary restrictions, and medical food-related needs at registration. We rely on the accuracy of parent-provided information. While we minimise cross-contact, an allergen-free environment cannot be absolutely guaranteed. For severe allergies/anaphylaxis, parents must provide clear instructions and any required medication. Organisers may contact emergency services if needed.",
  },
  {
    title: "Privacy Policy",
    body: "Parent/guardian consent is required to register a child and collect personal information. Information collected is used only for registration, safety, communication, and program administration. We do not sell personal information. Parents may contact us regarding their information. Photo/video consent is opt-in per child; no child will be featured in marketing if the parent/guardian did not consent.",
  },
  {
    title: "Media Release / Photo-Video Consent",
    body: "Photography and video may be taken during camp activities. Use in marketing or social media is only permitted for children whose parent/guardian has explicitly opted in. You may revoke this consent in writing at any time.",
  },
  {
    title: "Code of Conduct / Behaviour Policy",
    body: "We expect kind, respectful behaviour from all campers, parents, guardians, and staff. Organisers may refuse participation, remove a child from an activity, or contact a parent/guardian where health, safety, conduct, or supervision concerns arise. Repeated violations may lead to removal without refund (note: registration is already non-refundable as stated in the No-Refund Policy above).",
  },
];

const PolicyFooter = () => {
  const [open, setOpen] = useState(null);
  return (
    <section className="py-12 px-6" style={{ backgroundColor: C.purpleDeep, color: "white" }} data-testid="policy-footer">
      <div className="max-w-4xl mx-auto">
        <h3 className="text-center font-black text-xl mb-2" style={{ color: C.gold }}>Policies & Disclosures</h3>
        <p className="text-center text-white/70 text-sm mb-6">Click any policy to read the summary. Plain-English overview — not legal advice.</p>
        <div className="space-y-2">
          {POLICIES.map((p, i) => (
            <div key={i} className="rounded-xl overflow-hidden" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)" }}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-white/5 transition-colors"
                data-testid={`policy-${i}`}
              >
                <span className="font-bold text-sm sm:text-base">{p.title}</span>
                <ChevronDown className={`w-5 h-5 transition-transform ${open === i ? "rotate-180" : ""}`} style={{ color: C.gold }} />
              </button>
              <AnimatePresence initial={false}>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <p className="px-4 pb-4 text-sm text-white/80 leading-relaxed">{p.body}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
        <div className="mt-8 text-center">
          <p className="font-black text-base mb-1">Mix &amp; Mingle Events</p>
          <p className="text-white/70 text-sm italic">Create. Connect. Celebrate.</p>
          <p className="text-white/50 text-xs mt-2">© 2026 Mix &amp; Mingle Events · mixmingleeventsofficial@gmail.com</p>
        </div>
      </div>
    </section>
  );
};

export default MarketCampPage;
